// ==============================
// File: src/utils/globalSearch.js
// Description: Robust global search with routes, section-name match,
// field-level matching, primitive-item support, and fallbacks
// Works with either a Vault-like object (list/getJSON) or your new
// StorageContext adapter (readJSON/loadJSON).
// Also reads mirrored localStorage keys like "mudo:<path>".
// ==============================

const SECTION_ROUTES = [
  { match: ["product", "products", "gallery"], path: "/gallery" },
  { match: ["task", "planner", "gantt", "todo"], path: "/planner/tasks" },
  { match: ["staff", "team", "mudostaff", "mudo_staff"], path: "/staff" },
  { match: ["material", "materials", "mudo_materials"], path: "/materials" },
  { match: ["po", "purchase", "purchase-orders", "purchase_orders"], path: "/purchase-orders" },
  { match: ["batch", "batches", "kiln", "kilnlogs"], path: "/batches" },
  { match: ["workshop", "workshops"], path: "/workshops" },
  { match: ["piece", "pieces", "my-pieces", "mypieces"], path: "/my-pieces" },
  { match: ["receipt", "receipts"], path: "/receipts" },
  { match: ["packaging rules"], path: "/packaging/rules" },
  { match: ["packaging"], path: "/packaging/library" },
  { match: ["settings sound"], path: "/settings/sound" },
  { match: ["settings staff", "staff targets"], path: "/settings/staff-targets" },
  { match: ["settings data", "data & backups", "backup"], path: "/settings/data" },
  { match: ["analytics", "studio analytics"], path: "/analytics" },
  { match: ["time logs", "timelog"], path: "/time-logs" },
  { match: ["inspiration", "inspo", "board"], path: "/inspiration" },
  { match: ["shop", "market"], path: "/shop" },
  { match: ["forum"], path: "/forum" },
  { match: ["settings"], path: "/settings" },
  { match: ["debug storage", "storage debug"], path: "/debug/storage" },
];

const FALLBACK_KEYS = [
  // core
  "settings", "products", "tasks", "staff", "materials", "purchaseOrders",
  "batches", "kilnLogs", "workshops", "pieces", "packagingLibrary", "receipts",
  "subscriptionPlan",
  // legacy/common alternates
  "mudoTasks", "mudoStaff", "mudoMaterials", "POs", "participants",
];

// Per-file paths we commonly use with StorageContext (mirrored to localStorage as mudo:<path>)
const FILE_PATH_KEYS = [
  "materials.json",
  "packaging/items.json",
  "packaging/rules.json",
  "mudoStaff.json",
  "mudoUtilities.json",
  "planner/events.json",
  "planner/plannerTasks.json",
  "tasks/daily.json",
  "products.json",
  "inspiration/items.json",
  "purchase-orders/orders.json",
  "workshops/workshops.json",
  "workshops/bookings.json",
  "participants/pieces.json",
  "kiln/logs.json",
  "kiln/kilnLogs.json",
  "shopping/shoppingList.json",
  "timelog/logs.json",
  "timelog/active.json",
  "settings/mudoSettings.json",
  "studio/studioProfile.json",
  "auth/profile.json",
];

const FIELD_ORDER = [
  "fullName", "displayName", "name", "title", "productName", "materialName",
  "sku", "code", "email", "status", "type", "id",
];

function routeForSection(sectionKey = "") {
  const s = String(sectionKey).toLowerCase();
  for (const r of SECTION_ROUTES) {
    if (r.match.some((m) => s.includes(m))) return r.path;
  }
  return null;
}

function pickLabel(item) {
  for (const k of FIELD_ORDER) {
    const v = item?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  if (item?.firstName || item?.lastName) {
    return [item.firstName, item.lastName].filter(Boolean).join(" ").trim();
  }
  return "(unnamed)";
}

function fieldsText(item) {
  const parts = [];
  for (const k of FIELD_ORDER) {
    const v = item?.[k];
    if (typeof v === "string" && v.trim()) parts.push(v.trim());
  }
  const notes = item?.notes || item?.description;
  if (typeof notes === "string" && notes.trim()) parts.push(notes.trim());
  return parts.join(" • ");
}

function shortenAroundQuery(text, query, max = 120) {
  if (!text) return "";
  const t = String(text);
  const q = (query || "").toLowerCase();
  const i = q ? t.toLowerCase().indexOf(q) : -1;
  if (t.length <= max) return t;
  if (i < 0) return t.slice(0, max) + "…";
  const start = Math.max(0, i - Math.floor(max / 2));
  const end = Math.min(t.length, start + max);
  return (start > 0 ? "…" : "") + t.slice(start, end) + (end < t.length ? "…" : "");
}

async function tryGet(adapter, key, fallback = null) {
  // Supports vault.getJSON / storage.readJSON / storage.loadJSON
  try {
    if (adapter?.getJSON) {
      const v = await adapter.getJSON(key, fallback);
      return v == null ? fallback : v;
    }
    if (adapter?.readJSON) {
      const v = await adapter.readJSON(key, fallback);
      return v == null ? fallback : v;
    }
    if (adapter?.loadJSON) {
      const v = await adapter.loadJSON(key, fallback);
      return v == null ? fallback : v;
    }
  } catch {}
  return fallback;
}

async function safeList(adapter) {
  const out = {};

  // 1) Try vault.list() if present (VaultProvider)
  try {
    const all = await adapter?.list?.();
    if (all && typeof all === "object" && Object.keys(all).length) return all;
  } catch {}

  // 2) Fallback over common key buckets (Vault-style)
  for (const k of FALLBACK_KEYS) {
    const val = await tryGet(adapter, k, null);
    if (val != null) out[k] = val;
  }

  // 3) StorageContext file paths (per-file saves)
  for (const path of FILE_PATH_KEYS) {
    // Prefer direct read via adapter
    const val = await tryGet(adapter, path, null);
    if (val != null) {
      out[path] = val;
      continue;
    }
    // Mirror from localStorage (StorageContext.saveJSON writes mudo:<path>)
    try {
      const mirror = localStorage.getItem(`mudo:${path}`);
      if (mirror != null) out[path] = JSON.parse(mirror);
    } catch {}
  }

  // 4) Last resort: plain localStorage for FALLBACK_KEYS
  try {
    for (const k of FALLBACK_KEYS) {
      if (out[k] != null) continue;
      const s = window.localStorage?.getItem(k);
      if (s) out[k] = JSON.parse(s);
    }
  } catch {}

  return out;
}

export async function globalSearch(query, adapter) {
  if (!query || !query.trim()) return [];
  const q = query.toLowerCase();
  const allData = await safeList(adapter);
  const results = [];
  const sectionHeaderAdded = new Set(); // only one section-level result per section

  for (const [sectionKey, value] of Object.entries(allData)) {
    if (value == null) continue;
    const items = Array.isArray(value) ? value : [value];

    const sectionHits = String(sectionKey).toLowerCase().includes(q);
    const path = routeForSection(sectionKey) || null;

    for (const rawItem of items) {
      const item = (rawItem && typeof rawItem === "object")
        ? rawItem
        : { name: String(rawItem) };

      const fieldText = fieldsText(item);
      const fieldHit = fieldText.toLowerCase().includes(q);

      let rawHit = false;
      if (!fieldHit && !sectionHits) {
        try { rawHit = JSON.stringify(item).toLowerCase().includes(q); } catch {}
      }

      if (!(fieldHit || rawHit || sectionHits)) continue;

      // PRIORITIZE SECTION-LEVEL RESULTS (App Components: Widgets, Pages, Screens)
      if (!fieldHit && !rawHit && sectionHits && !sectionHeaderAdded.has(sectionKey)) {
        sectionHeaderAdded.add(sectionKey);
        results.push({
          section: sectionKey,
          label: `Open ${String(sectionKey).replace(/[_-]/g, " ")}`,
          snippet: `Go to ${path || "/"}`,
          path,
          data: null,
          _score: 1000, // Very high score for component/section results
          _isComponent: true,
        });
      } else if (fieldHit || rawHit) {
        results.push({
          section: sectionKey,
          label: pickLabel(item),
          snippet: shortenAroundQuery(fieldText || JSON.stringify(item), query),
          path,
          data: item,
          _score: (fieldHit ? 3 : 0) + (rawHit ? 1 : 0) + (sectionHits ? 1 : 0),
          _isComponent: false,
        });
      }
    }
  }

  const seen = new Set();
  const unique = results.filter((r) => {
    const k = `${r.section}__${r.label}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  unique.sort((a, b) => {
    // Components (widgets/pages) always come first
    if (a._isComponent !== b._isComponent) {
      return b._isComponent ? 1 : -1;
    }
    // Then by score
    if (b._score !== a._score) return b._score - a._score;
    // Then alphabetically by section
    if (a.section !== b.section) return String(a.section).localeCompare(String(b.section));
    // Finally by label
    return String(a.label).localeCompare(String(b.label));
  });

  return unique.slice(0, 15);
}
